import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { MultiSelect } from '@/components/ui/multi-select';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

type MultiSelectOption = { label: string; value: string; searchValue?: string };

interface FormMultiSelectFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label?: string;
  placeholder: string;
  description?: React.ReactNode;
  required?: boolean;
  options: MultiSelectOption[];
  /** Forwarded to `MultiSelect` — caps selection at N values. */
  maxSelected?: number;
  /** When false, only existing options can be selected. */
  creatable?: boolean;
  className?: string;
  onSearch?: (value: string) => void;
}

export const FormMultiSelectField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  required,
  options,
  maxSelected,
  creatable,
  className,
  onSearch,
}: FormMultiSelectFieldProps<TFieldValues>) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem className={className}>
        {label && (
          <FormLabel>
            {label} {required && <span className="text-primary">*</span>}
          </FormLabel>
        )}
        <FormControl>
          <MultiSelect
            options={options}
            selected={(field.value as string[]) || []}
            onChange={field.onChange}
            placeholder={placeholder}
            maxSelected={maxSelected}
            creatable={creatable}
            onSearch={onSearch}
          />
        </FormControl>
        {description && <FormDescription>{description}</FormDescription>}
        <FormMessage />
      </FormItem>
    )}
  />
);
FormMultiSelectField.displayName = 'FormMultiSelectField';
